import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../../constants/api";
import { useNavigate } from "react-router-dom";
import StatusBadge from "../ui/StatusBadge.jsx";
import CollapsibleBlock from "../ui/CollapsibleBlock.jsx";
import EntityLinkCard, {
  EntityLinkArchivedToggle,
  EntityLinkList,
  entityLinkArchiviertChip,
} from "../ui/EntityLinkCard.jsx";
import { OFFERTE_EMOJI, emojiForProjektTyp, labelForProjektTyp } from "../../constants/projektTypVisuals.js";

/* Archiv-Flag robust ermitteln (wie bei Projekten) */
const isArchiviert = (x) => {
  const s = (x?.status || x?.status_override || "").toString().toLowerCase().trim();
  return Boolean(
    x?.archiviert === true ||
      x?.archived === true ||
      x?.archiviert_at ||
      x?.archived_at ||
      x?.archiviert_am ||
      s === "archiviert"
  );
};

/* Safe Date → timestamp */
const toTime = (v) => {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
};

function offerteSubtitle(o, { showObjektSubline }) {
  const typ = String(o.typ || "offerte").toLowerCase();
  const typLabel = typ === "offerte" ? "Offerte" : labelForProjektTyp(typ);
  const typEmoji = typ === "offerte" ? OFFERTE_EMOJI : emojiForProjektTyp(typ);
  return [
    `${typEmoji} ${typLabel}`,
    showObjektSubline && o.objekt_name ? o.objekt_name : null,
    o._date || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function OffertenLinkList({ items, onOpen, showObjektSubline = false, archived = false }) {
  if (!items.length) return null;
  return (
    <EntityLinkList scrollable={items.length > 6}>
      {items.map((o) => (
        <EntityLinkCard
          key={o.id}
          muted={archived}
          title={o.titel || "Offerte"}
          subtitle={offerteSubtitle(o, { showObjektSubline })}
          onClick={() => onOpen(o)}
          badge={
            archived ? entityLinkArchiviertChip : o.status ? <StatusBadge status={o.status} entity="offerte" /> : null
          }
        />
      ))}
    </EntityLinkList>
  );
}

export default function KundenBlockOfferten({
  kundenId,
  objektId = null,
  /** "kunde" = Kundenkarte (Objektname in Zeile); "objekt" = Objektkarte */
  scope = "kunde",
}) {
  const [offerten, setOfferten] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const abortRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await api.get("/offerten", {
          signal: controller.signal,
          params: {
            ...(kundenId ? { kunde_id: kundenId } : {}),
            ...(objektId ? { objekt_id: objektId } : {}),
            archiv: "all",
            limit: 500,
          },
        });
        const raw = Array.isArray(res.data?.items) ? res.data.items : Array.isArray(res.data) ? res.data : [];
        const fmt = new Intl.DateTimeFormat("de-CH", {
          dateStyle: "short",
          timeStyle: "short",
        });
        const enriched = raw.map((o) => {
          const ts = toTime(o.updated_at || o.erstellt_am || o.gueltig_bis);
          const arch = isArchiviert(o);
          const dateStr = ts ? fmt.format(new Date(ts)) : "";
          return {
            ...o,
            _ts: ts,
            _arch: arch,
            _date: dateStr,
          };
        });
        if (!controller.signal.aborted && mounted) {
          setOfferten(enriched);
        }
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || e?.message === "canceled") {
          return;
        }
        if (mounted) setOfferten([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, [kundenId, objektId]);

  const sorted = useMemo(
    () =>
      [...offerten].sort((a, b) => {
        if (a._arch !== b._arch) return a._arch - b._arch;
        return (b._ts || 0) - (a._ts || 0);
      }),
    [offerten]
  );

  const aktive = sorted.filter((o) => !o._arch);
  const archivierte = sorted.filter((o) => o._arch);
  const isObjektScope = scope === "objekt";
  const infoRight = `${aktive.length} Offerten`;
  const panelId = isObjektScope ? "objekt-offerten-panel" : "kunden-offerten-panel";
  const persistKey = isObjektScope ? null : `kunden_offerten_open_${kundenId}`;
  const archivPersistKey = isObjektScope ? null : `kunden_arch_offerten_${kundenId}`;
  const blockTitle = isObjektScope ? `${OFFERTE_EMOJI} Offerten` : `${OFFERTE_EMOJI} Verknüpfte Offerten`;
  const showObjektSubline = scope === "kunde";

  const openOfferte = useCallback(
    (o) => {
      const id = o?.id;
      if (!id) return;
      navigate(`/offerten/${id}`);
    },
    [navigate]
  );

  const emptyMsg = isObjektScope ? "Keine Offerten verknüpft." : "Keine Offerten vorhanden.";

  return (
    <CollapsibleBlock
      compact={isObjektScope}
      title={blockTitle}
      headerInfo={infoRight}
      panelId={panelId}
      contentClassName={isObjektScope ? "px-3 sm:px-4 pb-3 sm:pb-4" : "px-4 sm:px-6 pb-4 sm:pb-5"}
      defaultOpen={false}
      {...(persistKey ? { persistKey } : {})}
      className={isObjektScope ? "" : "mt-3"}
    >
      {loading ? (
        <p className="text-sm py-2 text-blue-200/70">Lade…</p>
      ) : aktive.length === 0 && archivierte.length === 0 ? (
        <p className="text-sm py-2 text-blue-200/70">{emptyMsg}</p>
      ) : (
        <>
          {aktive.length > 0 && (
            <OffertenLinkList items={aktive} onOpen={openOfferte} showObjektSubline={showObjektSubline} />
          )}
          {archivierte.length > 0 && (
            <EntityLinkArchivedToggle
              label={`${archivierte.length} archivierte Offerten`}
              {...(archivPersistKey ? { persistKey: archivPersistKey } : {})}
            >
              <OffertenLinkList
                items={archivierte}
                onOpen={openOfferte}
                showObjektSubline={showObjektSubline}
                archived
              />
            </EntityLinkArchivedToggle>
          )}
        </>
      )}
    </CollapsibleBlock>
  );
}
